import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, AlertCircle, Linkedin, Instagram, Youtube, Play, ExternalLink, Check } from 'lucide-react';
import { sendQuizPayload } from '../lib/quiz-webhook';
import { trackLead } from '../lib/tracking';
import { LiquidCard, CardContent } from './ui/liquid-glass-card';

// Social do finalu miekkiego (te same profile co AboutSection/Footer)
const socialLinks = [
  { icon: Linkedin, href: 'https://www.linkedin.com/in/dawid-st', label: 'LinkedIn Dawida' },
  { icon: Instagram, href: 'https://www.instagram.com/_stepiendawid/', label: 'Instagram Dawida' },
  { icon: Youtube, href: 'https://www.youtube.com/@DAIEN-d', label: 'YouTube DAIEN' },
];

// Booking Google. Do osadzenia schedulera podmien na pelny URL "Embed" z Google
// (calendar.google.com/calendar/appointments/schedules/...?gv=true). Krotki link
// moze byc blokowany w iframe - wtedy dziala fallback "otworz w nowej karcie".
const BOOKING_URL = 'https://calendar.app.google/4V6jKtrVUtMihmoE9';
const BOOKING_EMBED_URL = BOOKING_URL;

// Benefity na ekranie audytu - bol -> rozwiazanie (NIE technologia, klient chce rozwiazania problemow)
const benefits = [
  {
    title: 'Nie tracisz już ani jednego zgłoszenia',
    desc: 'Telefon albo formularz o 22:00 czy w weekend? Odebrane i obsłużone od ręki. Klient nie zdąży zadzwonić do konkurencji.',
  },
  {
    title: 'Żaden klient Ci nie umyka',
    desc: 'Codzienne przypomnienia pilnują, do kogo oddzwonić i kogo dopiąć. Koniec z "zapomniałem się odezwać".',
  },
  {
    title: 'Montaże idą gładko',
    desc: 'Twoi technicy mają wsparcie na etapie prac. Mniej przestojów, telefonów "co teraz" i poprawek.',
  },
  {
    title: 'Pełna kontrola nad firmą',
    desc: 'Wszystkie zapytania i klienci w jednym miejscu od pierwszego dnia. Widzisz na czym stoisz, bez kartek i rozsypanego Excela.',
  },
];

// 3 punkty na finalnej stronie (H2H)
const thankYouPoints = [
  'Stawiam wyłącznie na długoterminowe relacje, partnerstwo i wspólny networking. Chcę, żeby współpraca była jak najbardziej przyjemna i żebyśmy razem rozwijali nasze biznesy.',
  'Bardzo zależy mi na Twoim sukcesie, bo Twój sukces jest też moim sukcesem.',
  'Z pasji do technologii: jeśli będziesz mieć z czymś problem, odezwij się śmiało, a na pewno pomogę, nawet jeśli to nie będzie stricte biznes. Wierzę, że biznes to nie B2B, tylko H2H (human to human). Uwielbiam kontakty z ludźmi, z którymi mogę zbudować coś wspaniałego. Z kilkoma osobami mam już świetne relacje i chętnie nawiążę je też z Tobą.',
];

// --- DATA ---
const Q4_VALUES: Record<string, number> = { 
  '0-10': 5, '11-30': 20, '31-100': 65, '100+': 150 
};
const Q5_HOURS: Record<string, number> = {
  '0-1h': 0.5, '1-3h': 2, '3-5h': 4, '5-15h': 10, '15h+': 20
};
const INDUSTRY_DEAL_VALUE: Record<string, number> = {
  hvac: 5000, solar: 25000, ecom: 600,
  b2b_consulting: 15000, services: 3500, other: 5000
};
// Klienci traceni miesiecznie przez nieodebrane telefony / wolny kontakt (srodek przedzialu)
const Q6_LOST: Record<string, number> = {
  '0-1': 1, '2-3': 3, '4-6': 5, '7+': 8
};
const HOURLY_RATE = 120; // zl/h - wartosc godziny pracy zespolu

const questions = [
  // Part 1
  {
    id: 'q1',
    title: 'Branża',
    hint: 'Wybierz główny obszar działalności.',
    options: [
      { label: 'HVAC', value: 'hvac', desc: 'Klimatyzacja, wentylacja, pompy ciepła' },
      { label: 'Sklep internetowy', value: 'ecom', desc: 'Sprzedaż online (e-commerce)' },
      { label: 'Fotowoltaika', value: 'solar', desc: 'Panele i instalacje OZE' },
      { label: 'Doradztwo B2B', value: 'b2b_consulting', desc: 'Usługi i konsulting dla firm' },
      { label: 'Usługi lokalne', value: 'services', desc: 'Np. remonty, beauty, gastronomia' },
      { label: 'Inne', value: 'other', desc: 'Twoja branża jest gdzie indziej' }
    ]
  },
  {
    id: 'q2',
    title: 'Wielkość zespołu',
    hint: 'Ilu ludzi liczy Twój obecny zespół?',
    options: [
      { label: '1 osoba', value: '1' },
      { label: '2-5', value: '2-5' },
      { label: '6-15', value: '6-15' },
      { label: '16+', value: '16+' }
    ]
  },
  {
    id: 'q3',
    title: 'Główny problem',
    hint: 'Co najwięcej Cię teraz blokuje?',
    options: [
      { label: 'Pozyskiwanie nowych klientów', value: 'Lead gen', desc: 'Za mało zapytań i nowych kontaktów do firmy' },
      { label: 'Obsługa klienta', value: 'Support', desc: 'Powtarzalne pytania i zgłoszenia zjadają czas' },
      { label: 'Nieodebrane telefony', value: 'Voice', desc: 'Dzwonią, nie odbierasz na czas i tracisz zlecenie' },
      { label: 'Ręczna, powtarzalna robota', value: 'Manual', desc: 'Przepisywanie danych, raporty i e-maile na piechotę' },
      { label: 'Inne', value: 'other', desc: 'Coś innego, opowiesz mi na rozmowie' }
    ]
  },
  // Part 2
  {
    id: 'q4',
    title: 'Ile leadów/m-c miesięcznie pozyskujesz teraz?',
    hint: 'Szacunkowa liczba nowych zapytań.',
    options: [
      { label: '0-10', value: '0-10' },
      { label: '11-30', value: '11-30' },
      { label: '31-100', value: '31-100' },
      { label: '100+', value: '100+' }
    ]
  },
  {
    id: 'q5',
    title: 'Ile godzin tygodniowo tracisz na powtarzalne zadania?',
    hint: 'Pomyśl o przepisywaniu danych, raportach, e-mailach i odpisywaniu na te same pytania.',
    options: [
      { label: '0-1h', value: '0-1h' },
      { label: '1-3h', value: '1-3h' },
      { label: '3-5h', value: '3-5h' },
      { label: '5-15h', value: '5-15h' },
      { label: '15h+', value: '15h+' }
    ]
  },
  {
    id: 'q6',
    title: 'Ilu klientów miesięcznie tracisz przez nieodebrane telefony lub zbyt wolny kontakt?',
    hint: 'Tylu, którzy zadzwonili albo napisali, ale nie doczekali się reakcji i poszli do konkurencji.',
    options: [
      { label: '0-1', value: '0-1' },
      { label: '2-3', value: '2-3' },
      { label: '4-6', value: '4-6' },
      { label: '7 lub więcej', value: '7+' }
    ]
  },
  {
    id: 'q7',
    title: 'Czy masz CRM/system do śledzenia leadów?',
    hint: 'Gdzie lądują kontakty z formularzy i telefonów?',
    options: [
      { label: 'Tak, używamy', value: 'yes' },
      { label: 'Tak, ale chaos', value: 'chaos' },
      { label: 'Excel', value: 'Excel' },
      { label: 'Nic', value: 'none' }
    ]
  },
  {
    id: 'q8',
    title: 'Co zrobisz z odzyskanymi godzinami?',
    hint: 'Wyobraź sobie dodatkowe 20h tygodniowo.',
    options: [
      { label: 'Rozwój produktu', value: 'product' },
      { label: 'Nowi klienci', value: 'clients' },
      { label: 'Czas wolny', value: 'time' },
      { label: 'Reinwestycja', value: 'reinvest' }
    ]
  }
];

// --- LOGIC ---
// Strata na klientach = ilu klientow/mies tracisz (q6) x wartosc klienta w branzy (q1)
function calcMonthlyLeadLoss(industry: string, q6: string): number {
  const lostPerMonth = Q6_LOST[q6] || 0;
  const dealValue = INDUSTRY_DEAL_VALUE[industry] || 5000;
  return lostPerMonth * dealValue;
}
function calcAnnualLeadLoss(industry: string, q6: string): number {
  return calcMonthlyLeadLoss(industry, q6) * 12;
}

function calcWastedHoursPerYear(q5: string): number {
  return (Q5_HOURS[q5] || 0) * 52; // godziny tygodniowo x 52 tygodnie
}

// Wskaznik efektywnosci 0-100 (im nizej, tym wiecej ucieka = wiekszy potencjal do odzyskania)
function calcBusinessScore(a: Record<string, string>): { score: number; label: string } {
  const pH: Record<string, number> = { '0-1h': 3, '1-3h': 8, '3-5h': 15, '5-15h': 25, '15h+': 35 };
  const pCRM: Record<string, number> = { yes: 0, chaos: 12, Excel: 18, none: 25 };
  const pM: Record<string, number> = { '0-1': 2, '2-3': 8, '4-6': 16, '7+': 24 };
  const pL: Record<string, number> = { '0-10': 2, '11-30': 6, '31-100': 10, '100+': 15 };
  const pP: Record<string, number> = { Voice: 8, 'Lead gen': 6, Support: 5, Manual: 5 };
  let s = 100 - (pH[a['q5']] ?? 10) - (pCRM[a['q7']] ?? 12) - (pM[a['q6']] ?? 8) - (pL[a['q4']] ?? 6) - (pP[a['q3']] ?? 4);
  s = Math.max(12, Math.min(92, s));
  const label = s < 40
    ? 'Dużo ucieka. Tu jest najwięcej do odzyskania.'
    : s < 65
    ? 'Solidna baza, ale sporo zostawiasz na stole.'
    : 'Nieźle poukładane. Zostają finalne usprawnienia.';
  return { score: s, label };
}

// Pierscien z wynikiem (SVG, paleta DAIEN)
function ScoreRing({ score }: { score: number }) {
  const r = 52;
  const c = 2 * Math.PI * r;
  const off = c - (score / 100) * c;
  return (
    <div className="relative w-32 h-32">
      <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
        <circle cx="60" cy="60" r={r} fill="none" stroke="hsl(var(--stroke))" strokeWidth="10" />
        <circle
          cx="60" cy="60" r={r} fill="none" stroke="hsl(var(--accent-to))" strokeWidth="10"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-light text-text-primary leading-none">{score}</span>
        <span className="text-xs text-muted mt-1">/ 100</span>
      </div>
    </div>
  );
}

// Wykres wzrostu przychodu (custom SVG, bez recharts). Os X = dni, Os Y = przychod/mc.
// Start = obecny przychod (baseline), koniec = projekcja (baseline + realny % wzrostu).
// Os Y zoomowana do okolic baseline (NIE od zera), zeby wzrost byl wyrazny niezaleznie od skali.
function GrowthChart({ baseline, projected }: { baseline: number; projected: number }) {
  const factors = [0, 0.06, 0.16, 0.32, 0.55, 0.8, 1]; // ease-in: plaski start -> wzrost do projekcji
  const n = factors.length;
  const top = projected > baseline ? projected : baseline * 1.25; // guard gdyby projected <= baseline
  const floor = baseline * 0.95;          // dno osi tuz pod obecnym przychodem (zoom)
  const span = (top - floor) || 1;
  const L = 92, R = 466, T = 30, B = 196; // wiecej miejsca po lewej na duze liczby
  const px = (i: number) => L + (i / (n - 1)) * (R - L);
  const py = (v: number) => B - ((v - floor) / span) * (B - T);
  const revenue = (i: number) => baseline + (top - baseline) * factors[i];
  const pts = factors.map((_, i) => ({ x: px(i), y: py(revenue(i)) }));
  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const area = `${line} L${R},${B} L${L},${B} Z`;
  return (
    <svg viewBox="0 0 480 232" className="w-full h-auto" role="img" aria-label="Wykres wzrostu przychodu po wdrożeniu">
      <defs>
        <linearGradient id="growthFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--accent-from))" stopOpacity="0.35" />
          <stop offset="100%" stopColor="hsl(var(--accent-from))" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Linie siatki: obecny przychod (dol) i docelowy (gora) - daleko od siebie, brak nakladania */}
      {[baseline, top].map((v, gi) => (
        <line key={gi} x1={L} x2={R} y1={py(v)} y2={py(v)} stroke="hsl(var(--stroke))" strokeWidth="1" />
      ))}
      <text x={L - 10} y={py(top) + 4} textAnchor="end" fontSize="11" fontWeight="600" fill="hsl(var(--accent-to))">{formatShort(top)}</text>
      <text x={L - 10} y={py(baseline) + 4} textAnchor="end" fontSize="11" fill="hsl(var(--muted))">{formatShort(baseline)}</text>
      <path d={area} fill="url(#growthFill)" />
      <path d={line} fill="none" stroke="hsl(var(--accent-to))" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={i === 0 || i === n - 1 ? 5 : 3} fill={i === 0 ? 'hsl(var(--muted))' : 'hsl(var(--accent-to))'} />
      ))}
      <text x={px(0)} y={B + 20} textAnchor="start" fontSize="11" fill="hsl(var(--muted))">dziś</text>
      <text x={px(3)} y={B + 20} textAnchor="middle" fontSize="11" fill="hsl(var(--muted))">90 dni</text>
      <text x={px(6)} y={B + 20} textAnchor="end" fontSize="11" fill="hsl(var(--muted))">180 dni</text>
      <text x={px(0) + 8} y={pts[0].y - 12} textAnchor="start" fontSize="12" fontWeight="600" fill="hsl(var(--muted))">Tu jesteś</text>
      <text x={px(6)} y={pts[6].y + 20} textAnchor="end" fontSize="12" fontWeight="600" fill="hsl(var(--accent-to))">Po współpracy</text>
    </svg>
  );
}

// Spersonalizowane obserwacje z odpowiedzi (custom raport kondycyjny)
function buildFindings(a: Record<string, string>, ctx: { wastedHours: number }): string[] {
  const f: string[] = [];
  const q7 = a['q7'];
  if (q7 === 'none') f.push('Bez systemu CRM kontakty giną, a to nawet 25% straconych leadów.');
  else if (q7 === 'Excel') f.push('Leady w Excelu to chaos i około 25% strat na obsłudze.');
  else if (q7 === 'chaos') f.push('CRM jest, ale panuje w nim chaos, więc część leadów wypada.');

  const lost = Q6_LOST[a['q6']];
  if (lost && lost >= 2) {
    f.push(`Tracisz nawet ${Math.round(lost)} klientów miesięcznie przez nieodebrane telefony. To bezpośrednia strata przychodu.`);
  }

  const wkly = Q5_HOURS[a['q5']];
  if (wkly && wkly >= 2) {
    f.push(`Około ${wkly} h tygodniowo na ręcznej robocie to ${ctx.wastedHours} h rocznie do odzyskania.`);
  }

  if ((Q4_VALUES[a['q4']] || 0) >= 30) {
    f.push('Przy Twoim wolumenie zapytań każdy nieobsłużony lead to realny ubytek przychodu.');
  }

  const q3 = a['q3'];
  if (q3 === 'Voice') f.push('Voicebot 24/7 odbiera nawet 95% połączeń i umawia wizyty sam.');
  else if (q3 === 'Lead gen') f.push('Szybki kontakt z leadem (poniżej minuty) potrafi podnieść konwersję nawet 3x.');
  else if (q3 === 'Support') f.push('Baza wiedzy AI (RAG) zdejmuje powtarzalne pytania z zespołu.');
  else if (q3 === 'Manual') f.push('Automatyzacja powtarzalnych zadań to Twój najszybszy quick win.');

  if (f.length === 0) {
    f.push('Automatyzacja powtarzalnych zadań to Twój najszybszy quick win.');
    f.push('Zaoszczędzony czas możesz od razu zainwestować we wzrost.');
  }
  return f.slice(0, 5);
}

// Format numbers nicely
const formatCurrency = (num: number) => {
  return new Intl.NumberFormat('pl-PL').format(Math.round(num));
};

// Kompaktowy format dla etykiet osi wykresu (zeby duze liczby sie miescily)
const formatShort = (v: number): string => {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2).replace('.', ',')} mln zł`;
  if (v >= 1_000) return `${Math.round(v / 1_000)} tys. zł`;
  return `${formatCurrency(v)} zł`;
};

// Wnioski (insights) - rekomendacje pod konkretne odpowiedzi
function computeInsights(a: Record<string, string>): string[] {
  const q3 = a['q3'] || '';
  const q4 = a['q4'] || '0-10';
  const q5 = a['q5'] || '0-1h';
  const q7 = a['q7'] || '';
  const out: string[] = [];
  if (q3 === 'Voice' && (Q5_HOURS[q5] || 0) >= 3) out.push('Voicebot 24/7 odbiera nawet 95% połączeń, a ROI widać zwykle w 2 miesiące.');
  if (q3 === 'Lead gen' && q4 === '100+') out.push('Twoja skala uzasadnia AI scoring leadów i integrację konwersji.');
  if (q7 === 'Excel' && (Q4_VALUES[q4] || 0) >= 30) out.push('Bez CRM tracisz minimum 25% leadów. Pierwszy krok to uporządkowany pipeline.');
  if (out.length === 0) {
    out.push('Automatyzacja powtarzalnych zadań to Twój najszybszy quick win.');
    out.push('Zaoszczędzony czas możesz od razu zainwestować we wzrost.');
  }
  return out;
}

// Wynik kwalifikacji na podstawie odpowiedzi invest/startWhen/decide
function getOutcome(a: Record<string, string>): 'book' | 'soft' | null {
  if (a['invest'] === 'no') return 'soft';
  if (a['invest'] === 'yes') {
    const w = a['startWhen'];
    if (w === 'today' || w === 'tomorrow') return 'book';
    if (w === 'week' || w === 'month') {
      if (a['decide'] === 'yes') return 'book';
      if (a['decide'] === 'no') return 'soft';
    }
  }
  return null;
}

// --- COMPONENT ---
type Phase = 'idle' | 'quiz' | 'qualify' | 'diagnosis' | 'contact' | 'report' | 'thankyou';

export default function QuizModule({
  isActive,
  onClose
}: {
  isActive: boolean;
  onClose: () => void;
}) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [qualifyStep, setQualifyStep] = useState<'invest' | 'when' | 'persuade'>('invest');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [contact, setContact] = useState({ firstName: '', lastName: '', email: '', phone: '', company: '', role: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (isActive && phase === 'idle') {
      setPhase('quiz');
      setCurrentQuestion(0);
      setQualifyStep('invest');
      setAnswers({});
      setContact({ firstName: '', lastName: '', email: '', phone: '', company: '', role: '' });
      setSubmitError(null);
    }
  }, [isActive, phase]);

  useEffect(() => {
    if (phase === 'quiz' || phase === 'qualify' || phase === 'diagnosis' || phase === 'contact' || phase === 'report' || phase === 'thankyou') {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [phase]);

  const handleAnswer = (value: string) => {
    if (phase !== 'quiz') return; // ochrona przed klikiem po przejsciu dalej
    const newAnswers = { ...answers, [questions[currentQuestion].id]: value };
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setTimeout(() => setCurrentQuestion(prev => Math.min(prev + 1, questions.length - 1)), 300);
    } else {
      setTimeout(() => { setQualifyStep('invest'); setPhase('qualify'); }, 300);
    }
  };

  // Wysylka pelnego payloadu (book po formularzu, soft od razu po kwalifikacji)
  const submitQuiz = async (a: Record<string, string>, outcome: 'book' | 'soft', c: typeof contact) => {
    const ind = a['q1'] || 'other';
    const q6 = a['q6'] || '0-1';
    const q5 = a['q5'] || '0-1h';
    const hours = calcWastedHoursPerYear(q5);
    await sendQuizPayload({
      submittedAt: new Date().toISOString(),
      contact: {
        firstName: c.firstName.trim(),
        lastName: c.lastName.trim(),
        email: c.email.trim(),
        phone: c.phone.trim(),
        company: c.company.trim(),
        role: c.role,
      },
      answers: a,
      diagnosis: {
        industry: ind,
        score: calcBusinessScore(a).score,
        annualLeadLoss: Math.round(calcAnnualLeadLoss(ind, q6)),
        wastedHours: hours,
        savingsValue: Math.round(hours * HOURLY_RATE),
        insights: computeInsights(a),
        outcome,
      },
      meta: {
        source: 'quiz',
        userAgent: navigator.userAgent,
        referrer: document.referrer,
        pageUrl: window.location.href,
      },
    });
    trackLead(outcome === 'book' ? 'quiz_book' : 'quiz_soft');
  };

  // Kwalifikacja. KAZDA terminalna odpowiedz prowadzi do formularza (zbieramy dane
  // niezaleznie od wyniku); wynik book/soft liczy getOutcome z answers.
  const handleQualify = (key: 'invest' | 'startWhen' | 'decide', value: string) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    if (key === 'invest') {
      if (value === 'yes') setQualifyStep('when');
      else setPhase('contact');
    } else if (key === 'startWhen') {
      if (value === 'today' || value === 'tomorrow') setPhase('contact');
      else setQualifyStep('persuade');
    } else {
      setPhase('contact');
    }
  };

  const resetQuiz = () => {
    setPhase('idle');
    setSubmitError(null);
    onClose();
  };

  const updateContact = (key: keyof typeof contact) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setContact((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);

    await submitQuiz(answers, getOutcome(answers) || 'soft', contact);

    setSubmitting(false);
    setPhase('diagnosis');
  };

  if (!isActive && phase === 'idle') return null;

  const q = questions[currentQuestion] ?? questions[questions.length - 1];
  const part = currentQuestion < 3 ? 1 : 2;

  // Compute Results / Diagnosis (miesiecznie + rocznie -> compound)
  let annualLeadLoss = 0;
  let monthlyLeadLoss = 0;
  let wastedHours = 0;       // rocznie
  let monthlyHours = 0;
  let annualTimeValue = 0;
  let monthlyTimeValue = 0;
  let annualTotal = 0;
  let monthlyTotal = 0;
  let score = 0;
  let scoreLabel = '';
  let findings: string[] = [];
  let lostPerMonth = 0;
  let dealVal = 0;
  let currentMonthlyRevenue = 0;
  let projectedRevenue = 0;

  if (phase === 'diagnosis') {
    const ind = answers['q1'] || 'other';
    const q6 = answers['q6'] || '0-1';
    const q5 = answers['q5'] || '0-1h';
    const q4 = answers['q4'] || '0-10';

    lostPerMonth = Q6_LOST[q6] || 0;
    dealVal = INDUSTRY_DEAL_VALUE[ind] || 5000;
    // Obecny przychod/mc = pozyskiwani klienci (q4) x wartosc klienta w branzy
    currentMonthlyRevenue = (Q4_VALUES[q4] || 0) * dealVal;
    monthlyLeadLoss = calcMonthlyLeadLoss(ind, q6);
    annualLeadLoss = monthlyLeadLoss * 12;
    wastedHours = calcWastedHoursPerYear(q5);
    monthlyHours = Math.round(wastedHours / 12);
    annualTimeValue = wastedHours * HOURLY_RATE;
    monthlyTimeValue = Math.round(annualTimeValue / 12);
    annualTotal = annualLeadLoss + annualTimeValue;
    monthlyTotal = monthlyLeadLoss + monthlyTimeValue;

    // Projekcja wzrostu jako realny % przychodu (proporcjonalny, conditional, zawsze widoczny).
    // % wynika z odzysku (monthlyTotal) wzgledem obecnego przychodu, ale clamp 20-40% zeby
    // bylo realne (nie absurd przy malym baseline) i zawsze widoczne (nie plaskie przy duzym).
    const rawRate = currentMonthlyRevenue > 0 ? monthlyTotal / currentMonthlyRevenue : 0.3;
    const growthRate = Math.min(0.4, Math.max(0.2, rawRate));
    projectedRevenue = Math.round(currentMonthlyRevenue * (1 + growthRate));

    const sc = calcBusinessScore(answers);
    score = sc.score;
    scoreLabel = sc.label;
    findings = buildFindings(answers, { wastedHours });
  }

  const outcome = getOutcome(answers);

  return (
    <AnimatePresence>
      {(phase === 'quiz' || phase === 'qualify' || phase === 'diagnosis' || phase === 'contact' || phase === 'report' || phase === 'thankyou') && (
        <motion.div
          className="fixed inset-0 z-50 bg-bg overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="max-w-[800px] mx-auto px-6 py-12 md:py-20 relative min-h-screen flex flex-col">
            

            {phase === 'quiz' && (
              <>
                {/* Progress Bar */}
                <div className="mb-16">
                  <div className="flex items-baseline justify-between mb-4">
                    <span className="text-sm font-medium text-text-primary uppercase tracking-widest">
                      Pytanie {currentQuestion + 1} z {questions.length}
                    </span>
                    <span className="text-xs text-muted uppercase tracking-widest">
                      Część {part} z 2
                    </span>
                  </div>
                  <div
                    className="flex gap-2"
                    role="progressbar"
                    aria-valuenow={currentQuestion + 1}
                    aria-valuemax={questions.length}
                  >
                    {questions.map((_, idx) => (
                      <div
                        key={idx}
                        className={`h-2 flex-1 rounded-full transition-colors duration-500 ${
                          idx < currentQuestion
                            ? 'bg-accent-from'
                            : idx === currentQuestion
                            ? 'bg-accent-from/60'
                            : 'bg-stroke/30'
                        }`}
                      />
                    ))}
                  </div>
                </div>

                {/* Question Area */}
                <div className="flex-grow flex flex-col justify-center">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={currentQuestion}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <h2 className="text-3xl md:text-5xl font-light text-text-primary mb-4 leading-tight">
                        {q.title}
                      </h2>
                      <p className="text-xl text-muted mb-12">
                        {q.hint}
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {q.options.map((opt, idx) => {
                          const desc = (opt as { desc?: string }).desc;
                          return (
                            <button
                              key={idx}
                              onClick={() => handleAnswer(opt.value)}
                              className="bg-surface hover:bg-text-primary hover:text-bg border border-stroke rounded-2xl p-6 text-left transition-colors w-full group"
                            >
                              <span className="block text-lg font-medium">{opt.label}</span>
                              {desc && (
                                <span className="block mt-1 text-sm font-normal opacity-70 leading-snug">{desc}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </>
            )}

            {phase === 'qualify' && (
              <motion.div
                className="flex-grow flex flex-col justify-center"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="text-center mb-10">
                  <div className="inline-flex items-center gap-3 mb-6">
                    <span className="w-8 h-px bg-stroke"></span>
                    <span className="text-xs text-muted uppercase tracking-[0.3em]">Jeszcze chwila</span>
                    <span className="w-8 h-px bg-stroke"></span>
                  </div>
                  <h2 className="text-3xl md:text-5xl font-light text-text-primary mb-4 leading-tight">
                    {qualifyStep === 'invest' && 'Czy jesteś w stanie zainwestować w systemy, które Ci pomogą?'}
                    {qualifyStep === 'when' && 'Kiedy chcesz zacząć?'}
                    {qualifyStep === 'persuade' && 'Zanim pójdziemy dalej'}
                  </h2>
                  <p className="text-lg text-muted max-w-xl mx-auto">
                    {qualifyStep === 'invest' && 'Bez zobowiązań. Chcę tylko wiedzieć, czy realnie myślisz o wdrożeniu.'}
                    {qualifyStep === 'when' && 'Im szybciej ruszymy, tym szybciej odzyskasz czas i pieniądze.'}
                    {qualifyStep === 'persuade' && 'Krótko, dlaczego warto nie odkładać tego na później.'}
                  </p>
                </div>

                {qualifyStep === 'invest' && (
                  <div className="grid grid-cols-1 gap-4 max-w-xl mx-auto w-full">
                    <button onClick={() => handleQualify('invest', 'yes')} className="bg-surface hover:bg-text-primary hover:text-bg border border-stroke rounded-2xl p-6 text-left transition-colors w-full text-lg font-medium">
                      Tak, chcę zainwestować w swoją firmę
                    </button>
                    <button onClick={() => handleQualify('invest', 'no')} className="bg-surface hover:bg-text-primary hover:text-bg border border-stroke rounded-2xl p-6 text-left transition-colors w-full text-lg font-medium">
                      Nie, na razie nie
                    </button>
                  </div>
                )}

                {qualifyStep === 'when' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl mx-auto w-full">
                    {[
                      { v: 'today', l: 'Dzisiaj' },
                      { v: 'tomorrow', l: 'Jutro' },
                      { v: 'week', l: 'Za tydzień' },
                      { v: 'month', l: 'Za miesiąc' },
                    ].map((o) => (
                      <button key={o.v} onClick={() => handleQualify('startWhen', o.v)} className="bg-surface hover:bg-text-primary hover:text-bg border border-stroke rounded-2xl p-6 text-left transition-colors w-full text-lg font-medium">
                        {o.l}
                      </button>
                    ))}
                  </div>
                )}

                {qualifyStep === 'persuade' && (
                  <div className="max-w-xl mx-auto w-full">
                    <div className="bg-surface border border-stroke rounded-3xl p-8 mb-6">
                      <p className="text-base md:text-lg text-text-primary/90 leading-relaxed">
                        Pracuję tylko z pojedynczymi osobami z danego regionu, żeby nie robić konkurencji moim klientom. To ma być ekskluzywna współpraca. Jeśli czujesz, że jesteś w tyle z technologią i to hamuje Cię przed zarobkami, których chcesz, to jest moment, żeby umówić spotkanie dziś lub jutro.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <button onClick={() => handleQualify('decide', 'yes')} className="bg-text-primary text-bg rounded-2xl p-6 text-center transition-transform hover:scale-[1.01] w-full text-lg font-medium">
                        Tak, umawiam się na dziś lub jutro
                      </button>
                      <button onClick={() => handleQualify('decide', 'no')} className="bg-surface hover:bg-bg border border-stroke text-text-primary rounded-2xl p-6 text-center transition-colors w-full text-lg font-medium">
                        Nie, jeszcze nie teraz
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {phase === 'diagnosis' && (
              <motion.div
                className="flex-grow flex flex-col justify-center"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="text-center mb-10">
                  <div className="inline-flex items-center gap-3 mb-6">
                    <span className="w-8 h-px bg-stroke"></span>
                    <span className="text-xs text-muted uppercase tracking-[0.3em]">Twoja diagnoza</span>
                    <span className="w-8 h-px bg-stroke"></span>
                  </div>
                  <h2 className="text-4xl md:text-5xl tracking-tight font-light text-text-primary mb-4">
                    Oto na czym <span className="font-display italic">tracisz</span>
                  </h2>
                  <p className="text-lg text-muted max-w-xl mx-auto">
                    Policzone na podstawie Twoich odpowiedzi.
                  </p>
                </div>

                {/* Wskaznik kondycji */}
                <div className="flex flex-col items-center mb-10">
                  <ScoreRing score={score} />
                  <div className="mt-4 text-center">
                    <div className="text-sm uppercase tracking-widest text-muted mb-1">Wskaźnik kondycji firmy</div>
                    <p className="text-text-primary max-w-md">{scoreLabel}</p>
                  </div>
                </div>

                {/* Laczna strata - efekt compound (liquid glass) */}
                <LiquidCard className="border-stroke rounded-3xl mb-6">
                  <CardContent className="p-8 text-center">
                    <div className="text-sm font-medium uppercase tracking-widest text-muted mb-3">Łącznie tracisz nawet</div>
                    <div className="font-mono text-5xl md:text-6xl text-text-primary mb-2">{formatCurrency(annualTotal)} PLN</div>
                    <div className="text-sm text-muted mb-6">rocznie, czyli ok. {formatCurrency(monthlyTotal)} PLN miesięcznie</div>
                    <div className="border-t border-stroke pt-5 max-w-md mx-auto text-left">
                      <div className="text-xs font-medium uppercase tracking-widest text-muted mb-3">Na co się składa</div>
                      <div className="space-y-4">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="text-sm font-medium text-text-primary">Stracone zlecenia</div>
                            <div className="text-xs text-muted mt-0.5">
                              {lostPerMonth} klientów × {formatCurrency(dealVal)} zł = {formatCurrency(monthlyLeadLoss)} zł/mc, × 12
                            </div>
                          </div>
                          <span className="font-mono text-sm text-text-primary shrink-0">{formatCurrency(annualLeadLoss)} PLN</span>
                        </div>
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="text-sm font-medium text-text-primary">Zmarnowany czas</div>
                            <div className="text-xs text-muted mt-0.5">
                              {formatCurrency(wastedHours)} h × {HOURLY_RATE} zł rocznie
                            </div>
                          </div>
                          <span className="font-mono text-sm text-text-primary shrink-0">{formatCurrency(annualTimeValue)} PLN</span>
                        </div>
                        <div className="flex items-center justify-between gap-4 border-t border-stroke pt-3 mt-1">
                          <span className="text-sm font-semibold text-text-primary">Razem rocznie</span>
                          <span className="font-mono text-sm font-semibold text-accent-to shrink-0">{formatCurrency(annualTotal)} PLN</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </LiquidCard>

                {/* Rozbicie: stracone zlecenia + zmarnowany czas (rocznie + miesiecznie) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-surface border-l-4 border-accent-to rounded-3xl p-8 shadow-lg">
                    <div className="text-sm text-muted font-medium mb-4">STRACONE ZLECENIA</div>
                    <div className="font-mono text-4xl md:text-5xl text-accent-to mb-1">
                      {formatCurrency(annualLeadLoss)} PLN
                    </div>
                    <div className="text-sm text-muted mb-3">rocznie · ok. {formatCurrency(monthlyLeadLoss)} PLN miesięcznie</div>
                    <p className="text-sm text-muted">Klienci, których tracisz przez nieodebrane telefony i zbyt wolny kontakt.</p>
                  </div>
                  <div className="bg-surface border-l-4 border-accent-from rounded-3xl p-8 shadow-lg">
                    <div className="text-sm text-muted font-medium mb-4">ZMARNOWANY CZAS</div>
                    <div className="font-mono text-4xl md:text-5xl text-accent-from mb-1">
                      {wastedHours} h
                    </div>
                    <div className="text-sm text-muted mb-3">rocznie ({monthlyHours} h/mc) · wartość ok. {formatCurrency(annualTimeValue)} PLN</div>
                    <p className="text-sm text-muted">Czas na ręcznej robocie, który możesz odzyskać.</p>
                  </div>
                </div>

                {/* Spersonalizowane obserwacje */}
                <div className="bg-surface border border-stroke rounded-3xl p-8 mb-10">
                  <div className="text-sm font-medium uppercase tracking-widest text-muted mb-6">Co widzę u Ciebie</div>
                  <ul className="space-y-4">
                    {findings.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-4 text-base md:text-lg text-text-primary/90 leading-relaxed">
                        <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-accent-to shrink-0" aria-hidden="true"></span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Co sie zmieni - benefity bol -> rozwiazanie */}
                <div className="mb-10">
                  <div className="text-center mb-6">
                    <div className="text-sm font-medium uppercase tracking-widest text-muted mb-2">Co się u Ciebie zmieni</div>
                    <p className="text-base text-muted max-w-xl mx-auto">Nie sprzedaję Ci technologii, tylko rozwiązanie tych problemów.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {benefits.map((b) => (
                      <div key={b.title} className="bg-surface border border-stroke rounded-2xl p-6">
                        <div className="flex items-start gap-4">
                          <span className="shrink-0 w-9 h-9 rounded-full bg-bg border border-stroke flex items-center justify-center">
                            <Check className="w-4 h-4 text-accent-to" />
                          </span>
                          <div>
                            <h4 className="font-semibold text-text-primary mb-1 leading-snug">{b.title}</h4>
                            <p className="text-sm text-muted leading-relaxed">{b.desc}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Wykres wzrostu przychodu po wdrozeniu */}
                <div className="bg-surface border border-stroke rounded-3xl p-6 md:p-8 mb-10">
                  <div className="text-sm font-medium uppercase tracking-widest text-muted mb-1">Tak rośnie Twój przychód</div>
                  <p className="text-sm text-muted mb-6">
                    Z obecnych ok. {formatCurrency(currentMonthlyRevenue)} zł/mc do ok. {formatCurrency(projectedRevenue)} zł/mc{currentMonthlyRevenue > 0 ? ` (+${Math.round((projectedRevenue / currentMonthlyRevenue - 1) * 100)}%)` : ''} w pół roku, po odzyskaniu straconych klientów i czasu. Prognoza (szacunek), realne tempo zależy od wdrożenia.
                  </p>
                  <GrowthChart baseline={currentMonthlyRevenue} projected={projectedRevenue} />
                </div>

                {outcome === 'book' ? (
                  <div className="text-center">
                    <button
                      onClick={() => setPhase('report')}
                      className="inline-flex items-center justify-center gap-2 bg-text-primary text-bg px-8 py-4 rounded-full text-base font-medium hover:scale-[1.01] transition-transform"
                    >
                      Umów spotkanie i odbierz plan →
                    </button>
                    <p className="text-xs text-muted mt-4">Wybierzesz termin, oddzwonię i potwierdzimy szczegóły.</p>
                  </div>
                ) : (
                  <div className="bg-surface border border-stroke rounded-3xl p-8 text-center">
                    <p className="text-base md:text-lg text-text-primary/90 leading-relaxed mb-6">
                      Nic nie szkodzi. Jeśli chcesz dowiedzieć się więcej, zapraszam na moje social media.
                    </p>
                    <div className="flex items-center justify-center gap-3">
                      {socialLinks.map((s) => {
                        const Icon = s.icon;
                        return (
                          <a
                            key={s.label}
                            href={s.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={s.label}
                            className="w-11 h-11 rounded-full bg-bg border border-stroke flex items-center justify-center text-muted hover:text-text-primary hover:border-accent-to transition-colors"
                          >
                            <Icon className="w-5 h-5" />
                          </a>
                        );
                      })}
                    </div>
                    <button onClick={resetQuiz} className="mt-6 text-sm text-muted hover:text-text-primary transition-colors">
                      Wróć na stronę główną
                    </button>
                  </div>
                )}
              </motion.div>
            )}

            {phase === 'contact' && (
              <motion.div
                className="flex-grow flex flex-col justify-center"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="text-center mb-10">
                  <div className="inline-flex items-center gap-3 mb-6">
                    <span className="w-8 h-px bg-stroke"></span>
                    <span className="text-xs text-muted uppercase tracking-[0.3em]">Ostatni krok</span>
                    <span className="w-8 h-px bg-stroke"></span>
                  </div>
                  <h2 className="text-4xl md:text-5xl tracking-tight font-light text-text-primary mb-4">
                    Zostaw kontakt, żeby zobaczyć swój <span className="font-display italic">audyt</span>
                  </h2>
                  <p className="text-lg text-muted max-w-xl mx-auto">
                    Pokażę Ci dokładnie, ile tracisz, ile czasu zaoszczędzisz i Twój wskaźnik kondycji firmy.
                  </p>
                </div>

                <form onSubmit={handleContactSubmit} className="max-w-xl mx-auto w-full space-y-4" noValidate>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="cf-firstName" className="block text-xs font-medium text-muted uppercase tracking-wider mb-2">
                        Imię
                      </label>
                      <input
                        id="cf-firstName"
                        name="firstName"
                        type="text"
                        required
                        autoComplete="given-name"
                        value={contact.firstName}
                        onChange={updateContact('firstName')}
                        className="w-full bg-surface border border-stroke rounded-xl px-4 py-3 text-base text-text-primary placeholder:text-muted focus:border-text-primary focus:outline-none transition-colors"
                        placeholder="Jan"
                      />
                    </div>
                    <div>
                      <label htmlFor="cf-lastName" className="block text-xs font-medium text-muted uppercase tracking-wider mb-2">
                        Nazwisko
                      </label>
                      <input
                        id="cf-lastName"
                        name="lastName"
                        type="text"
                        required
                        autoComplete="family-name"
                        value={contact.lastName}
                        onChange={updateContact('lastName')}
                        className="w-full bg-surface border border-stroke rounded-xl px-4 py-3 text-base text-text-primary placeholder:text-muted focus:border-text-primary focus:outline-none transition-colors"
                        placeholder="Kowalski"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="cf-email" className="block text-xs font-medium text-muted uppercase tracking-wider mb-2">
                      Email
                    </label>
                    <input
                      id="cf-email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      value={contact.email}
                      onChange={updateContact('email')}
                      className="w-full bg-surface border border-stroke rounded-xl px-4 py-3 text-base text-text-primary placeholder:text-muted focus:border-text-primary focus:outline-none transition-colors"
                      placeholder="email@firma.pl"
                    />
                  </div>
                  <div>
                    <label htmlFor="cf-phone" className="block text-xs font-medium text-muted uppercase tracking-wider mb-2">
                      Numer telefonu
                    </label>
                    <input
                      id="cf-phone"
                      name="phone"
                      type="tel"
                      required
                      autoComplete="tel"
                      pattern="[+\d\s\-()]{7,}"
                      value={contact.phone}
                      onChange={updateContact('phone')}
                      className="w-full bg-surface border border-stroke rounded-xl px-4 py-3 text-base text-text-primary placeholder:text-muted focus:border-text-primary focus:outline-none transition-colors"
                      placeholder="+48 600 000 000"
                    />
                  </div>
                  <div>
                    <label htmlFor="cf-company" className="block text-xs font-medium text-muted uppercase tracking-wider mb-2">
                      Nazwa firmy
                    </label>
                    <input
                      id="cf-company"
                      name="company"
                      type="text"
                      required
                      autoComplete="organization"
                      value={contact.company}
                      onChange={updateContact('company')}
                      className="w-full bg-surface border border-stroke rounded-xl px-4 py-3 text-base text-text-primary placeholder:text-muted focus:border-text-primary focus:outline-none transition-colors"
                      placeholder="Twoja Firma Sp. z o.o."
                    />
                  </div>
                  <div>
                    <label htmlFor="cf-role" className="block text-xs font-medium text-muted uppercase tracking-wider mb-2">
                      Twoje stanowisko
                    </label>
                    <select
                      id="cf-role"
                      name="role"
                      required
                      value={contact.role}
                      onChange={updateContact('role')}
                      className={`w-full bg-surface border border-stroke rounded-xl px-4 py-3 text-base appearance-none focus:border-text-primary focus:outline-none transition-colors ${contact.role ? 'text-text-primary' : 'text-muted'}`}
                    >
                      <option value="" disabled>Wybierz stanowisko</option>
                      <option value="wlasciciel">Właściciel / CEO</option>
                      <option value="dyrektor-zarzadzajacy">Dyrektor zarządzający</option>
                      <option value="dyrektor-sprzedazy">Dyrektor sprzedaży</option>
                      <option value="dyrektor-operacyjny">Dyrektor operacyjny</option>
                      <option value="kierownik">Kierownik / Manager</option>
                      <option value="technik">Technik / Monter</option>
                      <option value="inne">Inne</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-text-primary text-bg px-8 py-4 rounded-full text-base font-medium hover:scale-[1.01] transition-transform disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Wysyłam...
                      </>
                    ) : (
                      'Pokaż moją diagnozę →'
                    )}
                  </button>

                  {submitError && (
                    <div className="flex items-center justify-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
                      <AlertCircle className="w-4 h-4" />
                      {submitError}
                    </div>
                  )}

                  <p className="text-xs text-muted text-center leading-relaxed max-w-md mx-auto pt-2">
                    Wysyłając akceptujesz{' '}
                    <Link to="/polityka-prywatnosci" className="underline hover:text-text-primary">
                      politykę prywatności
                    </Link>
                    {' '}i{' '}
                    <Link to="/rodo" className="underline hover:text-text-primary">
                      klauzulę RODO
                    </Link>
                    . Dane służą wyłącznie do kontaktu telefonicznego (speed-to-lead).
                  </p>
                </form>
              </motion.div>
            )}

            {phase === 'report' && (
              <motion.div
                className="flex-grow flex flex-col justify-center"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="text-center mb-10">
                  <h2 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-text-primary mb-4">
                    Oto Twój <span className="font-display italic">audyt</span>
                  </h2>
                  <p className="text-lg text-muted max-w-xl mx-auto">
                    Wybierz termin spotkania poniżej. Oddzwonię w kilka minut i potwierdzimy szczegóły.
                  </p>
                </div>

                {/* Scheduler Google: iframe tylko dla pelnego URL embed (calendar.google.com).
                    Krotki link calendar.app.google ma X-Frame-Options=sameorigin (nie da sie ramkowac) -> karta z przyciskiem. */}
                {BOOKING_EMBED_URL.includes('calendar.google.com') ? (
                  <div className="relative w-full rounded-3xl overflow-hidden border border-stroke bg-surface mb-10 h-[560px]">
                    <iframe
                      src={BOOKING_EMBED_URL}
                      title="Rezerwacja spotkania"
                      className="absolute inset-0 w-full h-full"
                      frameBorder={0}
                    />
                  </div>
                ) : (
                  <div className="bg-surface border border-stroke rounded-3xl p-8 text-center mb-10">
                    <p className="text-base md:text-lg text-text-primary/90 leading-relaxed mb-6">
                      Otwórz kalendarz i wybierz termin, który Ci pasuje. Zajmie to chwilę.
                    </p>
                    <a
                      href={BOOKING_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center gap-2 bg-text-primary text-bg rounded-full px-8 py-4 hover:scale-105 transition-transform font-medium"
                    >
                      Otwórz kalendarz i wybierz termin
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                )}

                <div className="flex justify-center">
                  <button
                    onClick={() => setPhase('thankyou')}
                    className="bg-text-primary text-bg rounded-full px-8 py-4 hover:scale-105 transition-transform w-full sm:w-auto text-center font-medium"
                  >
                    Zarezerwowałem termin, dalej →
                  </button>
                </div>
              </motion.div>
            )}

            {phase === 'thankyou' && (
              <motion.div
                className="flex-grow flex flex-col justify-center"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="text-center mb-10">
                  <div className="inline-flex items-center gap-3 mb-6">
                    <span className="w-8 h-px bg-stroke"></span>
                    <span className="text-xs text-muted uppercase tracking-[0.3em]">Do zobaczenia na spotkaniu</span>
                    <span className="w-8 h-px bg-stroke"></span>
                  </div>
                  <h2 className="text-3xl md:text-4xl lg:text-5xl tracking-tight font-light text-text-primary mb-4 leading-tight">
                    Dziękuję bardzo. Podjąłeś bardzo dobrą <span className="font-display italic">decyzję</span> i postaram się, żebyś jej nie pożałował.
                  </h2>
                </div>

                {/* VSL placeholder */}
                <div className="relative w-full aspect-video rounded-3xl overflow-hidden border border-stroke bg-surface shadow-xl mb-10">
                  <div
                    className="absolute inset-0 flex flex-col items-center justify-center text-center p-8"
                    style={{
                      background:
                        'radial-gradient(circle at 30% 30%, hsl(var(--accent-from) / 0.25), transparent 60%), radial-gradient(circle at 70% 70%, hsl(var(--accent-to) / 0.2), transparent 60%), hsl(var(--surface))',
                    }}
                    aria-hidden="true"
                  >
                    <div className="w-20 h-20 rounded-full bg-text-primary/90 text-bg flex items-center justify-center mb-4 shadow-lg">
                      <Play className="w-8 h-8 fill-current ml-1" />
                    </div>
                    <p className="text-sm uppercase tracking-[0.25em] text-muted">Video wkrótce</p>
                    <p className="text-xs text-muted mt-2 max-w-xs">Krótkie wideo powitalne przed naszym spotkaniem</p>
                  </div>
                </div>

                <p className="text-lg text-muted mb-6 text-center">Oto 3 rzeczy, o których chcę, żebyś wiedział:</p>
                <div className="space-y-4">
                  {thankYouPoints.map((point, idx) => (
                    <div key={idx} className="bg-surface border border-stroke rounded-2xl p-6 flex items-start gap-5">
                      <span className="shrink-0 w-10 h-10 rounded-full bg-text-primary text-bg flex items-center justify-center font-medium">
                        {idx + 1}
                      </span>
                      <p className="text-base md:text-lg text-text-primary/90 leading-relaxed">{point}</p>
                    </div>
                  ))}
                </div>
                <div className="text-center mt-10">
                  <button onClick={resetQuiz} className="text-sm text-muted hover:text-text-primary transition-colors">
                    Wróć na stronę główną
                  </button>
                </div>
              </motion.div>
            )}

          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
